import { Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch, useSearchParams } from 'react-router';
import { ListToolbar } from '@/components/list/ListToolbar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePlaceContext, type PlaceContext } from '../place/PlaceLayout';
import { apiPlace, sectionPath } from '../place/place';
import { NamePanel, type NameSubmitResult } from '../ui/NamePanel';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { OrganizationListPane } from './OrganizationListPane';
import { useOrganizationActs } from './use-organization-acts';
import { useOrganizationNames } from './use-organization-names';
import {
  useOrganizations,
  type OrganizationFilters,
  type OrganizationRow,
} from './use-organizations';

export type { OrganizationRow };

/** What Organizzazioni hands its card (`OrganizationSheet`). */
export type OrganizationsContext = PlaceContext & {
  readonly items: readonly OrganizationRow[];
  readonly loading: boolean;
  readonly acts: ReturnType<typeof useOrganizationActs>;
  readonly onRename: (row: OrganizationRow) => void;
};

const ALL = 'all';
const STATUSES = ['active', 'archived'] as const;

function readFilters(params: URLSearchParams): OrganizationFilters {
  const status = params.get('status');
  return {
    q: params.get('q') ?? '',
    status: STATUSES.find((value) => value === status) ?? null,
  };
}

type SetParams = ReturnType<typeof useSearchParams>[1];

/** Tenant's «Crea la prima organizzazione» lands here with ?new=1: open the panel, once. */
function useOpenNewFromLink(
  params: URLSearchParams,
  setParams: SetParams,
  canCreate: boolean,
  setCreating: (open: boolean) => void,
) {
  useEffect(() => {
    if (params.get('new') === '1' && canCreate) {
      setCreating(true);
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('new');
          return next;
        },
        { replace: true },
      );
    }
  }, [params, setParams, canCreate]);
}

function useFilterSetters(setParams: SetParams) {
  const setFilter = useCallback(
    (key: 'q' | 'status', value: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null || value === '' || value === ALL) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const clearFilters = useCallback(() => setParams({}, { replace: true }), [setParams]);
  return { setFilter, clearFilters };
}

function PhoneCreate({ onCreate }: { readonly onCreate: () => void }) {
  const { t } = useTranslation('administration');
  return (
    <PhonePrimary>
      <Button onClick={onCreate}>
        <Plus />
        {t('organizations.new')}
      </Button>
    </PhonePrimary>
  );
}

function OrganizationsHead({
  titleId,
  tenantName,
  canCreate,
  onCreate,
  filters,
  setFilter,
  filtered,
  onClear,
}: {
  readonly titleId: string;
  readonly tenantName: string;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
  readonly filters: OrganizationFilters;
  readonly setFilter: (key: 'q' | 'status', value: string | null) => void;
  readonly filtered: boolean;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('organizations.title')}
        summary={t('organizations.summary', { tenant: tenantName })}
        actions={
          canCreate ? (
            <span className="page-primary">
              <Button onClick={() => onCreate()}>
                <Plus />
                {t('organizations.new')}
              </Button>
            </span>
          ) : undefined
        }
      />
      <OrganizationsToolbar
        filters={filters}
        setFilter={setFilter}
        filtered={filtered}
        onClear={onClear}
      />
    </div>
  );
}

function OrganizationNamePanels({
  tenantName,
  creating,
  setCreating,
  renaming,
  setRenaming,
  create,
  rename,
}: {
  readonly tenantName: string;
  readonly creating: boolean;
  readonly setCreating: (open: boolean) => void;
  readonly renaming: OrganizationRow | null;
  readonly setRenaming: (row: OrganizationRow | null) => void;
  readonly create: (name: string) => Promise<NameSubmitResult>;
  readonly rename: (row: OrganizationRow, name: string) => Promise<NameSubmitResult>;
}) {
  const { t } = useTranslation('administration');
  return (
    <>
      <NamePanel
        open={creating}
        onOpenChange={setCreating}
        title={t('organizations.create.title')}
        description={t('organizations.create.summary', { tenant: tenantName })}
        help={t('organizations.create.help')}
        note={t('organizations.create.note')}
        submitLabel={t('organizations.create.submit')}
        submitIcon={<Plus />}
        onSubmit={create}
      />
      <NamePanel
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenaming(null);
          }
        }}
        title={t('organizations.rename.title', { name: renaming?.name ?? '' })}
        initialName={renaming?.name ?? ''}
        help={t('organizations.rename.help')}
        submitLabel={t('organizations.rename.submit')}
        submitIcon={<Pencil />}
        onSubmit={(name) =>
          renaming === null ? Promise.resolve({ ok: true }) : rename(renaming, name)
        }
      />
    </>
  );
}

/**
 * Organizzazioni (FR-AD-6, the third round of boards in decision 61), at a tenant: its
 * organizations with their people, active and archived, and the acts on them — create (the code is
 * generated), rename, archive and restore. Archiving acts at once and says so; restoring is its
 * undo (the spec's derogation from the 6-second window). The two rows whose archival would shut
 * someone out offer no «Archivia» (contract 0.9.0), and say why.
 */
export function OrganizationsPage() {
  const { place, detail, refreshPlace } = usePlaceContext();
  const tenantId = apiPlace(place)?.placeId ?? '';
  const tenantName = detail.place.name;
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const { state, more, reload } = useOrganizations(tenantId, filters);
  const canCreate = detail.can.browseOrganizations;
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<OrganizationRow | null>(null);
  const titleId = useId();

  useOpenNewFromLink(params, setParams, canCreate, setCreating);
  const { setFilter, clearFilters } = useFilterSetters(setParams);
  const filtered = filters.q !== '' || filters.status !== null;

  const { create, rename } = useOrganizationNames(tenantId, reload);
  const acts = useOrganizationActs(tenantId, reload, refreshPlace);
  const items = state.items;
  const totals = state.status === 'ready' ? state.page.totals : null;
  const emptyTenant = totals !== null && totals.active + totals.archived === 0;
  const listPath = sectionPath(place, 'organizations');
  const sheetOpen = useMatch(`${listPath}/:organizationId`) !== null;
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const childContext: OrganizationsContext = {
    ...usePlaceContext(),
    items,
    loading: state.status === 'loading',
    acts,
    onRename: setRenaming,
  };

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section flex flex-col gap-5', sheetOpen && 'people-section-with-sheet')}
    >
      <OrganizationsHead
        titleId={titleId}
        tenantName={tenantName}
        canCreate={canCreate}
        onCreate={() => setCreating(true)}
        filters={filters}
        setFilter={setFilter}
        filtered={filtered}
        onClear={clearFilters}
      />
      <div className={cn('people-layout', sheetOpen && 'people-layout-with-sheet')}>
        <div className="people-list-pane">
          <OrganizationListPane
            state={state}
            more={more}
            reload={reload}
            filtered={filtered}
            emptyTenant={emptyTenant}
            canCreate={canCreate}
            tenantName={tenantName}
            acts={acts}
            onClear={clearFilters}
            onCreate={() => setCreating(true)}
            onRename={setRenaming}
            cardHrefFor={(row) => `${listPath}/${row.id}${query}`}
          />
        </div>
        <Outlet context={childContext} />
      </div>
      {canCreate ? <PhoneCreate onCreate={() => setCreating(true)} /> : null}
      <OrganizationNamePanels
        tenantName={tenantName}
        creating={creating}
        setCreating={setCreating}
        renaming={renaming}
        setRenaming={setRenaming}
        create={create}
        rename={rename}
      />
    </section>
  );
}

/** The shared filterable list's toolbar (decision 101): search by name, and the status as a label. */
function OrganizationsToolbar({
  filters,
  setFilter,
  onClear,
}: {
  readonly filters: OrganizationFilters;
  readonly setFilter: (key: 'q' | 'status', value: string | null) => void;
  readonly filtered: boolean;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <ListToolbar
      label={t('organizations.title')}
      search={{
        label: t('organizations.searchLabel'),
        value: filters.q,
        onWrite: (q) => setFilter('q', q),
      }}
      fields={[
        {
          key: 'status',
          label: t('organizations.statusFilter'),
          options: STATUSES.map((status) => ({
            value: status,
            label: t(`organizations.statusOption.${status}`),
          })),
        },
      ]}
      active={{ status: filters.status }}
      onFilter={(key, value) => setFilter(key === 'status' ? 'status' : 'q', value)}
      onClear={onClear}
    />
  );
}
